'use client';
'use no memo';

import React, { useEffect, useState } from 'react';

export interface TapCheckoutModalProps {
  contentClassName?: string;
  contentStyle?: React.CSSProperties;
  isOpen: boolean;
  onClose: () => void;
  chargeId: string;
  redirectUrl?: string;
  onSuccess?: (chargeId: string) => void;
  onError?: (error: Error) => void;
}

/**
 * Tap Checkout Modal
 *
 * This component handles the Tap payment flow by redirecting to Tap's hosted checkout page.
 * According to Tap's documentation, the checkout SDK provides a hosted payment page.
 */
export function TapCheckoutModal({
  contentClassName,
  contentStyle,
  isOpen,
  onClose,
  chargeId,
  redirectUrl,
  onSuccess: _onSuccess,
  onError: _onError,
}: TapCheckoutModalProps) {
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, _setError] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen && chargeId && redirectUrl) {
      // Redirect to Tap's checkout page
      setIsProcessing(true);
      window.location.href = redirectUrl;
    }
  }, [isOpen, chargeId, redirectUrl]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      {/* Backdrop */}
      <div
        className="bg-opacity-50 fixed inset-0 bg-black transition-opacity"
        onClick={!isProcessing ? onClose : undefined}
      />

      {/* Modal */}
      <div className="flex min-h-full items-center justify-center p-4">
        <div
          className={`relative w-full max-w-md rounded-2xl bg-white shadow-xl ${contentClassName ?? ''}`}
          style={contentStyle}
        >
          {/* Header */}
          <div className="p-6 pb-4">
            <div className="flex items-start justify-between">
              <div>
                <h2 className="text-2xl font-semibold text-gray-900">
                  Redirecting to Payment
                </h2>
                <p className="mt-1 text-sm text-gray-500">
                  Please wait while we redirect you to complete your payment
                  securely
                </p>
              </div>
              {!isProcessing && (
                <button
                  onClick={onClose}
                  className="-mt-1 -mr-1 text-gray-400 transition-colors hover:text-gray-500"
                >
                  <svg
                    className="h-6 w-6"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              )}
            </div>
          </div>

          {/* Content */}
          <div className="px-6 pb-6">
            <div className="py-8 text-center">
              <div className="mx-auto mb-4 h-12 w-12 animate-spin rounded-full border-b-2 border-gray-600"></div>
              <p className="text-gray-600">Redirecting to secure payment...</p>
              <p className="mt-2 text-sm text-gray-500">
                Please do not close this window
              </p>
            </div>

            {error && (
              <div className="mt-4 rounded-lg border border-red-200 bg-red-50 p-4">
                <p className="text-sm text-red-800">{error}</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
